import { getWebSocketUrl } from "src/config";
import { authTokenService } from "./authTokenService";

export interface ApiClientError extends Error {
  status?: number;
  body?: unknown;
}

/** Auth mode for API calls - `"optional"` attaches JWT when present (OptionalJwtAuthGuard routes). */
export type ApiAuthMode = boolean | "optional";

export interface ApiRequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  headers?: Record<string, string>;
  body?: unknown;
  /**
   * Auth requirement:
   * - `true` (default): JWT required, fails locally if missing
   * - `false`: never send Authorization
   * - `"optional"`: send Bearer when a token exists (public + enriched for logged-in users)
   */
  requiresAuth?: ApiAuthMode;
  /** When false, 401 responses are not retried after refresh. */
  retryOnUnauthorized?: boolean;
}

type ResolvedRequestOptions = ApiRequestOptions & {
  requiresAuth: ApiAuthMode;
  retryOnUnauthorized: boolean;
};

/**
 * HTTP client for the web app API with automatic JWT refresh on 401.
 */
class ApiClient {
  private readonly baseUrl = getWebSocketUrl();

  /**
   * Performs an authenticated fetch with optional 401 refresh and single retry.
   *
   * @param endpoint - Path (e.g. `/users/me`) or absolute URL.
   * @param init - Standard fetch init (body, method, headers).
   * @param options - Auth and retry flags.
   * @returns Raw fetch Response.
   */
  async authorizedFetch(
    endpoint: string,
    init: RequestInit = {},
    options: Pick<
      ApiRequestOptions,
      "requiresAuth" | "retryOnUnauthorized"
    > = {},
  ): Promise<Response> {
    const resolved: ResolvedRequestOptions = {
      requiresAuth: options.requiresAuth ?? true,
      retryOnUnauthorized: options.retryOnUnauthorized ?? true,
    };

    const url = this.resolveUrl(endpoint);
    const response = await this.executeFetch(url, init, resolved);

    // Skip refresh when auth is off, or optional with no session to refresh
    const canRetryWithRefresh =
      resolved.requiresAuth === true ||
      (resolved.requiresAuth === "optional" &&
        Boolean(authTokenService.getAccessToken()));
    if (
      response.status !== 401 ||
      !canRetryWithRefresh ||
      !resolved.retryOnUnauthorized ||
      this.isAuthEndpoint(url)
    ) {
      return response;
    }
    const newToken = await authTokenService.refreshAccessToken(this.baseUrl);
    if (!newToken) {
      return response;
    }
    return this.executeFetch(url, init, resolved, newToken);
  }

  /**
   * JSON API request with automatic parsing and error handling.
   *
   * @param endpoint - API path or URL.
   * @param options - Request configuration.
   * @returns Parsed JSON body.
   */
  async request<T>(
    endpoint: string,
    options: ApiRequestOptions = {},
  ): Promise<T> {
    const {
      method = "GET",
      headers = {},
      body,
      requiresAuth = true,
      retryOnUnauthorized = true,
    } = options;

    const init: RequestInit = {
      method,
      headers: { ...headers },
    };

    const isFormData =
      typeof FormData !== "undefined" && body instanceof FormData;
    if (body !== undefined && method !== "GET") {
      if (isFormData) {
        init.body = body;
      } else {
        init.headers = {
          ...init.headers,
          "Content-Type": "application/json",
        };
        init.body = JSON.stringify(body);
      }
    }

    const response = await this.authorizedFetch(endpoint, init, {
      requiresAuth,
      retryOnUnauthorized,
    });

    if (response.status === 204) {
      return undefined as T;
    }

    const raw = await response.text();
    const payload =
      raw.trim().length > 0 ? (JSON.parse(raw) as unknown) : undefined;

    if (!response.ok) {
      const error = new Error(
        Array.isArray((payload as { message?: unknown })?.message)
          ? (payload as { message: unknown[] }).message.map(String).join(" · ")
          : typeof (payload as { message?: string })?.message === "string"
            ? (payload as { message: string }).message
            : `HTTP ${response.status}`,
      ) as ApiClientError;
      error.status = response.status;
      error.body = payload;
      throw error;
    }

    return payload as T;
  }

  async get<T>(
    endpoint: string,
    options: Omit<ApiRequestOptions, "method" | "body"> = {},
  ): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: "GET" });
  }

  async post<T>(
    endpoint: string,
    body?: unknown,
    options: Omit<ApiRequestOptions, "method" | "body"> = {},
  ): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: "POST", body });
  }

  async put<T>(
    endpoint: string,
    body?: unknown,
    options: Omit<ApiRequestOptions, "method" | "body"> = {},
  ): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: "PUT", body });
  }

  async patch<T>(
    endpoint: string,
    body?: unknown,
    options: Omit<ApiRequestOptions, "method" | "body"> = {},
  ): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: "PATCH", body });
  }

  async delete<T>(
    endpoint: string,
    options: Omit<ApiRequestOptions, "method" | "body"> = {},
  ): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: "DELETE" });
  }

  private resolveUrl(endpoint: string): string {
    if (/^https?:\/\//.test(endpoint)) {
      return endpoint;
    }
    const path = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
    return `${this.baseUrl}${path}`;
  }

  private isAuthEndpoint(url: string): boolean {
    return url.includes("/auth/sign-in") || url.includes("/auth/refresh");
  }

  private async executeFetch(
    url: string,
    init: RequestInit,
    options: ResolvedRequestOptions,
    forcedAccessToken?: string,
  ): Promise<Response> {
    const headers = new Headers(init.headers ?? {});

    if (!headers.has("X-Client-Type")) {
      headers.set("X-Client-Type", "WEB");
    }

    if (options.requiresAuth === true || options.requiresAuth === "optional") {
      const accessToken =
        forcedAccessToken ?? authTokenService.getAccessToken();
      // Hard auth: fail before the network when no token is available
      if (!accessToken && options.requiresAuth === true) {
        return new Response(
          JSON.stringify({ message: "Token d'authentification manquant" }),
          {
            status: 401,
            headers: { "Content-Type": "application/json" },
          },
        );
      }
      // Optional auth: attach Bearer only when a session exists
      if (accessToken) {
        headers.set("Authorization", `Bearer ${accessToken}`);
      }
    }
    return fetch(url, {
      ...init,
      headers,
      credentials: init.credentials ?? "include",
    });
  }
}

export const apiClient = new ApiClient();
